import { useEffect, useRef, useState, useCallback } from 'react';
import BpmnModeler from 'bpmn-js/lib/Modeler';
import type { BpmnModelerInstance } from '../types/bpmn.types';
import { bpmnApi } from '../api/bpmnApi';
import { amtModdleDescriptor } from '../utils/amtModdleDescriptor';
import type { AmtTaskDisplay, AmtFormDefinition, AmtProcessConfig } from '../utils/amtModdleDescriptor';
import { flowableModdleDescriptor } from '../utils/flowableModdleDescriptor';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Moddle = { create: (type: string, attrs?: Record<string, any>) => any };

/**
 * Serialize an AmtTaskDisplay into a moddle element tree.
 */
function serializeTaskDisplay(moddle: Moddle, data: AmtTaskDisplay) {
  return moddle.create('amt:TaskDisplay', {
    priority: data.priority,
    icon: data.icon || undefined,
    color: data.color || undefined,
    labels: Object.entries(data.labels)
      .filter(([, v]) => v)
      .map(([lang, value]) => moddle.create('amt:Label', { lang, value })),
    descriptions: Object.entries(data.descriptions)
      .filter(([, v]) => v)
      .map(([lang, value]) => moddle.create('amt:Description', { lang, value })),
  });
}

/**
 * Serialize an AmtFormDefinition into a moddle element tree.
 */
function serializeFormDefinition(moddle: Moddle, data: AmtFormDefinition) {
  return moddle.create('amt:FormDefinition', {
    id: data.id,
    sections: data.sections.map((section) =>
      moddle.create('amt:Section', {
        id: section.id,
        collapsible: section.collapsible || undefined,
        collapsed: section.collapsed || undefined,
        titles: Object.entries(section.titles)
          .filter(([, v]) => v)
          .map(([lang, value]) => moddle.create('amt:Title', { lang, value })),
        descriptions: Object.entries(section.descriptions)
          .filter(([, v]) => v)
          .map(([lang, value]) => moddle.create('amt:Description', { lang, value })),
        fields: section.fields.map((field) =>
          moddle.create('amt:Field', {
            id: field.id,
            type: field.type,
            processVariable: field.processVariable || undefined,
            required: field.required || undefined,
            readOnly: field.readOnly || undefined,
            defaultValue: field.defaultValue || undefined,
            labels: Object.entries(field.labels)
              .filter(([, v]) => v)
              .map(([lang, value]) => moddle.create('amt:Label', { lang, value })),
            placeholders: Object.entries(field.placeholders)
              .filter(([, v]) => v)
              .map(([lang, value]) => moddle.create('amt:Placeholder', { lang, value })),
            hints: Object.entries(field.hints)
              .filter(([, v]) => v)
              .map(([lang, value]) => moddle.create('amt:Hint', { lang, value })),
            options:
              field.options.length > 0
                ? moddle.create('amt:Options', {
                    option: field.options.map((opt) =>
                      moddle.create('amt:Option', {
                        value: opt.value,
                        labels: Object.entries(opt.labels)
                          .filter(([, v]) => v)
                          .map(([lang, value]) => moddle.create('amt:Label', { lang, value })),
                      })
                    ),
                  })
                : undefined,
            validation:
              field.validationRules.length > 0
                ? moddle.create('amt:Validation', {
                    rules: field.validationRules.map((rule) =>
                      moddle.create('amt:Rule', {
                        type: rule.type,
                        value: rule.value || undefined,
                        messages: Object.entries(rule.messages)
                          .filter(([, v]) => v)
                          .map(([lang, value]) => moddle.create('amt:Message', { lang, value })),
                      })
                    ),
                  })
                : undefined,
            conditional: field.conditional
              ? moddle.create('amt:Conditional', {
                  field: field.conditional.field,
                  operator: field.conditional.operator,
                  value: field.conditional.value,
                })
              : undefined,
          })
        ),
      })
    ),
  });
}

/**
 * Serialize an AmtProcessConfig into a moddle element tree.
 */
function serializeProcessConfig(moddle: Moddle, data: AmtProcessConfig) {
  return moddle.create('amt:ProcessConfig', {
    returnChannelEnabled: data.returnChannelEnabled,
  });
}

export interface BpmnElement {
  id: string;
  type: string;
  businessObject: {
    id: string;
    name?: string;
    documentation?: Array<{ text: string }>;
    $type: string;
    $attrs?: Record<string, unknown>;
    extensionElements?: {
      values?: Array<{
        $type: string;
        [key: string]: unknown;
      }>;
    };
    // Flowable Service Task properties
    class?: string;
    expression?: string;
    delegateExpression?: string;
    resultVariable?: string;
    topic?: string;
    // Flowable User Task properties
    assignee?: string;
    candidateUsers?: string;
    candidateGroups?: string;
    dueDate?: string;
    formKey?: string;
    priority?: string;
    // Script Task
    scriptFormat?: string;
    script?: string;
    // Sequence Flow
    conditionExpression?: {
      body?: string;
      $type: string;
    };
    sourceRef?: { id: string };
    targetRef?: { id: string };
    // Gateway
    default?: { id: string };
    [key: string]: unknown;
  };
}

interface UseBpmnModelerOptions {
  xml?: string;
  processDefinitionKey?: string;
  keyId?: string;
  isOwnProcess?: boolean;
  onChange?: (xml: string) => void;
}

interface UseBpmnModelerResult {
  containerRef: React.RefObject<HTMLDivElement | null>;
  propertiesPanelRef: React.RefObject<HTMLDivElement | null>;
  modeler: BpmnModelerInstance | null;
  isLoading: boolean;
  error: string | null;
  isDirty: boolean;
  canUndo: boolean;
  canRedo: boolean;
  selectedElement: BpmnElement | null;
  elementRevision: number;
  getXml: () => Promise<string | null>;
  undo: () => void;
  redo: () => void;
  save: () => Promise<boolean>;
  zoomIn: () => void;
  zoomOut: () => void;
  fitViewport: () => void;
  exportSvg: () => Promise<string | null>;
  createNewDiagram: () => void;
  updateElementProperty: (property: string, value: unknown) => void;
  updateElementName: (name: string) => void;
  updateElementDocumentation: (text: string) => void;
  updateAmtExtension: (amtType: 'TaskDisplay' | 'FormDefinition' | 'ProcessConfig', data: AmtTaskDisplay | AmtFormDefinition | AmtProcessConfig, targetElement?: BpmnElement) => void;
  getProcessElement: () => BpmnElement | null;
}

const EMPTY_DIAGRAM = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
                  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
                  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
                  xmlns:di="http://www.omg.org/spec/DD/20100524/DI"
                  id="Definitions_1"
                  targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn:process id="Process_1" isExecutable="true">
    <bpmn:startEvent id="StartEvent_1" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1">
      <bpmndi:BPMNShape id="_BPMNShape_StartEvent_1" bpmnElement="StartEvent_1">
        <dc:Bounds x="179" y="159" width="36" height="36" />
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

export function useBpmnModeler({
  xml,
  processDefinitionKey,
  keyId,
  isOwnProcess = false,
  onChange,
}: UseBpmnModelerOptions): UseBpmnModelerResult {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const propertiesPanelRef = useRef<HTMLDivElement | null>(null);
  const modelerRef = useRef<BpmnModelerInstance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [selectedElement, setSelectedElement] = useState<BpmnElement | null>(null);
  const [elementRevision, setElementRevision] = useState(0);

  // Initialize modeler
  useEffect(() => {
    if (!containerRef.current) return;

    const modeler = new BpmnModeler({
      container: containerRef.current,
      moddleExtensions: {
        amt: amtModdleDescriptor,
        flowable: flowableModdleDescriptor,
      },
    });

    modelerRef.current = modeler;

    // Expose modeler for E2E testing
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__bpmnModeler = modeler;

    // Listen for element selection
    modeler.on('selection.changed', (e: unknown) => {
      const event = e as { newSelection: BpmnElement[] };
      if (event.newSelection && event.newSelection.length === 1) {
        setSelectedElement(event.newSelection[0]);
      } else {
        setSelectedElement(null);
      }
    });

    // Listen for changes
    modeler.on('commandStack.changed', () => {
      setIsDirty(true);
      const commandStack = modeler.get('commandStack') as {
        canUndo: () => boolean;
        canRedo: () => boolean;
      };
      setCanUndo(commandStack.canUndo());
      setCanRedo(commandStack.canRedo());

      // Notify parent of changes
      if (onChange) {
        modeler.saveXML().then(({ xml }) => {
          if (xml) onChange(xml);
        });
      }

      // Signal that element properties changed so consumers re-render.
      // We keep the original bpmn-js element reference (not a shallow copy)
      // to avoid breaking modeling.updateProperties and to prevent the
      // IntegratedPropertiesPanel useEffect from re-firing unnecessarily.
      setElementRevision((r) => r + 1);
    });

    let cancelled = false;

    const loadDiagram = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let diagramXml = xml;

        if (!diagramXml && processDefinitionKey) {
          diagramXml = await bpmnApi.getProcessDefinitionXml(processDefinitionKey);
        }

        if (cancelled) return;

        if (!diagramXml) {
          diagramXml = EMPTY_DIAGRAM;
        }

        await modeler.importXML(diagramXml);

        if (cancelled) return;

        const canvas = modeler.get('canvas') as { zoom: (level: string | number) => void };
        canvas.zoom('fit-viewport');
        setIsDirty(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load BPMN diagram:', err);
        setError(err instanceof Error ? err.message : 'Fehler beim Laden des BPMN-Diagramms');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadDiagram();

    return () => {
      cancelled = true;
      modeler.destroy();
      modelerRef.current = null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (window as any).__bpmnModeler;
    };
  }, [xml, processDefinitionKey]);

  const getXml = useCallback(async (): Promise<string | null> => {
    const modeler = modelerRef.current;
    if (!modeler) return null;
    try {
      const result = await modeler.saveXML({ format: true });
      return result.xml || null;
    } catch (err) {
      console.error('Failed to get XML:', err);
      return null;
    }
  }, []);

  const undo = useCallback(() => {
    const modeler = modelerRef.current;
    if (!modeler) return;
    const commandStack = modeler.get('commandStack') as { undo: () => void };
    commandStack.undo();
  }, []);

  const redo = useCallback(() => {
    const modeler = modelerRef.current;
    if (!modeler) return;
    const commandStack = modeler.get('commandStack') as { redo: () => void };
    commandStack.redo();
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (!processDefinitionKey) return false;

    try {
      const currentXml = await getXml();
      if (!currentXml) return false;

      if (isOwnProcess && keyId) {
        await bpmnApi.updateMyDefinition(keyId, processDefinitionKey, currentXml);
      } else {
        await bpmnApi.updateDefinition(processDefinitionKey, currentXml);
      }
      setIsDirty(false);
      return true;
    } catch (err) {
      console.error('Failed to save diagram:', err);
      return false;
    }
  }, [processDefinitionKey, keyId, isOwnProcess, getXml]);

  const zoomIn = useCallback(() => {
    const modeler = modelerRef.current;
    if (!modeler) return;
    const canvas = modeler.get('canvas') as { zoom: (level: string | number, center?: string) => number };
    const currentZoom = canvas.zoom('fit-viewport');
    canvas.zoom(currentZoom * 1.2);
  }, []);

  const zoomOut = useCallback(() => {
    const modeler = modelerRef.current;
    if (!modeler) return;
    const canvas = modeler.get('canvas') as { zoom: (level: string | number, center?: string) => number };
    const currentZoom = canvas.zoom('fit-viewport');
    canvas.zoom(currentZoom * 0.8);
  }, []);

  const fitViewport = useCallback(() => {
    const modeler = modelerRef.current;
    if (!modeler) return;
    const canvas = modeler.get('canvas') as { zoom: (level: string) => void };
    canvas.zoom('fit-viewport');
  }, []);

  const exportSvg = useCallback(async (): Promise<string | null> => {
    const modeler = modelerRef.current;
    if (!modeler) return null;
    try {
      const result = await modeler.saveSVG();
      return result.svg;
    } catch (err) {
      console.error('Failed to export SVG:', err);
      return null;
    }
  }, []);

  const createNewDiagram = useCallback(async () => {
    const modeler = modelerRef.current;
    if (!modeler) return;

    try {
      await modeler.importXML(EMPTY_DIAGRAM);
      const canvas = modeler.get('canvas') as { zoom: (level: string) => void };
      canvas.zoom('fit-viewport');
      setIsDirty(true);
    } catch (err) {
      console.error('Failed to create new diagram:', err);
    }
  }, []);

  const updateElementProperty = useCallback((property: string, value: unknown) => {
    const modeler = modelerRef.current;
    if (!modeler || !selectedElement) return;

    const modeling = modeler.get('modeling') as {
      updateProperties: (element: BpmnElement, properties: Record<string, unknown>) => void;
    };

    modeling.updateProperties(selectedElement, { [property]: value });
  }, [selectedElement]);

  const updateElementName = useCallback((name: string) => {
    const modeler = modelerRef.current;
    if (!modeler || !selectedElement) return;

    const modeling = modeler.get('modeling') as {
      updateLabel: (element: BpmnElement, newLabel: string) => void;
    };

    modeling.updateLabel(selectedElement, name);
  }, [selectedElement]);

  const updateElementDocumentation = useCallback((text: string) => {
    const modeler = modelerRef.current;
    if (!modeler || !selectedElement) return;

    const modeling = modeler.get('modeling') as {
      updateProperties: (element: BpmnElement, properties: Record<string, unknown>) => void;
    };
    const moddle = modeler.get('moddle') as {
      create: (type: string, attrs?: Record<string, unknown>) => { text: string };
    };

    const documentation = text ? [moddle.create('bpmn:Documentation', { text })] : [];
    modeling.updateProperties(selectedElement, { documentation });
  }, [selectedElement]);

  const getProcessElement = useCallback((): BpmnElement | null => {
    const modeler = modelerRef.current;
    if (!modeler) return null;

    const canvas = modeler.get('canvas') as { getRootElement: () => BpmnElement };
    const rootElement = canvas.getRootElement();
    // The root element on the canvas is typically a bpmn:Process or bpmn:Collaboration
    // For a collaboration, we need to get the participant's process
    if (rootElement?.businessObject?.$type === 'bpmn:Process') {
      return rootElement;
    }
    return rootElement;
  }, []);

  const updateAmtExtension = useCallback(
    (amtType: 'TaskDisplay' | 'FormDefinition' | 'ProcessConfig', data: AmtTaskDisplay | AmtFormDefinition | AmtProcessConfig, targetElement?: BpmnElement) => {
      const modeler = modelerRef.current;
      if (!modeler) return;

      const element = targetElement || selectedElement;
      if (!element) return;

      const moddle = modeler.get('moddle') as Moddle;
      const modeling = modeler.get('modeling') as {
        updateProperties: (element: BpmnElement, properties: Record<string, unknown>) => void;
      };

      // Serialize the data into a moddle element tree
      let moddleElement;
      if (amtType === 'TaskDisplay') {
        moddleElement = serializeTaskDisplay(moddle, data as AmtTaskDisplay);
      } else if (amtType === 'FormDefinition') {
        moddleElement = serializeFormDefinition(moddle, data as AmtFormDefinition);
      } else {
        moddleElement = serializeProcessConfig(moddle, data as AmtProcessConfig);
      }

      // For ProcessConfig, target the process root element
      const targetEl = amtType === 'ProcessConfig' ? (getProcessElement() || element) : element;

      // Get or create extensionElements
      const bo = targetEl.businessObject;
      const existingValues = bo.extensionElements?.values || [];

      // Replace existing element of same type, keep others
      const filteredValues = existingValues.filter(
        (v: { $type: string }) => v.$type !== `amt:${amtType}`
      );
      filteredValues.push(moddleElement);

      const extensionElements = moddle.create('bpmn:ExtensionElements', {
        values: filteredValues,
      });

      modeling.updateProperties(targetEl, { extensionElements });
    },
    [selectedElement, getProcessElement]
  );

  return {
    containerRef,
    propertiesPanelRef,
    modeler: modelerRef.current,
    isLoading,
    error,
    isDirty,
    canUndo,
    canRedo,
    selectedElement,
    elementRevision,
    getXml,
    undo,
    redo,
    save,
    zoomIn,
    zoomOut,
    fitViewport,
    exportSvg,
    createNewDiagram,
    updateElementProperty,
    updateElementName,
    updateElementDocumentation,
    updateAmtExtension,
    getProcessElement,
  };
}
